import axios from "axios";

export interface ApiErrorResponse {
    message: string;
    status?: number;
    fieldErrors?: Record<string, string[]>;
}

export const getApiError = (error: unknown): ApiErrorResponse => {
    if (!axios.isAxiosError(error)) {
        return {
            message: "Something went wrong. Please try again.",
        };
    }

    const status = error.response?.status;
    const data = error.response?.data;

    /*
     * -----------------------------------------
     * Validation errors
     *
     * {
     *   email: ["Enter a valid email address."]
     * }
     * -----------------------------------------
     */
    if (data && typeof data === "object") {
        const fieldErrors: Record<string, string[]> = {};

        Object.entries(data).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                fieldErrors[key] = value.map(String);
            }
        });

        if (Object.keys(fieldErrors).length > 0) {
            const firstError = Object.values(fieldErrors)[0]?.[0];

            return {
                message: firstError || "Please check the entered information.",
                status,
                fieldErrors,
            };
        }
    }

    /*
     * -----------------------------------------
     * OTP error
     *
     * {
     *   error: "Invalid or expired OTP",
     *   details: "OTP not found..."
     * }
     * -----------------------------------------
     */
    if (data?.error) {
        return {
            message: String(data.error),
            status,
        };
    }

    /*
     * -----------------------------------------
     * DRF detail error
     * -----------------------------------------
     */
    if (data?.detail) {
        return {
            message: getFriendlyDetailMessage(
                String(data.detail),
                status,
            ),
            status,
        };
    }

    /*
     * -----------------------------------------
     * Generic message
     * -----------------------------------------
     */
    if (data?.message) {
        return {
            message: String(data.message),
            status,
        };
    }

    /*
     * -----------------------------------------
     * HTTP status fallback
     * -----------------------------------------
     */
    return {
        message: getStatusMessage(status),
        status,
    };
};


const getFriendlyDetailMessage = (
    detail: string,
    status?: number,
): string => {

    if (
        detail.toLowerCase().includes("token not valid") ||
        detail.toLowerCase().includes("token is invalid") ||
        detail.toLowerCase().includes("token is expired")
    ) {
        return "Your session has expired. Please sign in again.";
    }

    if (detail.toLowerCase().includes("method") &&
        detail.toLowerCase().includes("not allowed")) {
        return "This action is not supported.";
    }

    if (detail.toLowerCase().includes("not found")) {
        return "The requested information could not be found.";
    }

    return detail || getStatusMessage(status);
};


const getStatusMessage = (status?: number): string => {
    switch (status) {
        case 400:
            return "Please check the information you entered.";

        case 401:
            return "Your session has expired. Please sign in again.";

        case 403:
            return "You do not have permission to perform this action.";

        case 404:
            return "The requested information could not be found.";

        case 405:
            return "This action is not supported.";

        case 408:
            return "The request timed out. Please try again.";

        case 409:
            return "This request conflicts with existing information.";

        case 422:
            return "Some of the information is invalid.";

        case 429:
            return "Too many requests. Please try again later.";

        case 500:
        case 502:
        case 503:
        case 504:
            return "The server is temporarily unavailable. Please try again later.";

        default:
            return "Something went wrong. Please try again.";
    }
};